/**
 * Admin Tennis Transfer Panel — admin-only UI for performing a transfer on a
 * squad owner's behalf when the site is down or a user has texted in a swap.
 *
 * Reuse contract:
 *   - The actual transfer math, validation, and Firestore write are owned by
 *     TennisTransferModal + utils/tennisTransfers (already battle-tested).
 *   - This panel only selects a league + squad, fetches the pool, and opens
 *     the existing modal with `adminContext` set so the resulting history
 *     entry is stamped with `actedByAdminUid`.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import type { League, LeagueSquad, User } from '../../types/database';
import {
  tennisSquadService,
  tennisPlayerPoolService,
  userService,
} from '../../services/firestore';
import { useAuth } from '../../contexts/AuthContext';
import {
  getTennisLeagueOptionsForAdminTransfer,
  getTennisSquadOptionsForAdminTransfer,
} from '../../utils/adminTennisTransfer';
import TennisTransferModal from '../tennis/TennisTransferModal';
import type { PlayerPoolEntry } from '../../utils/tennisTransfers';

type Props = {
  leagues: League[];
};

const AdminTennisTransferPanel: React.FC<Props> = ({ leagues }) => {
  const { user } = useAuth();

  const tennisLeagues = useMemo(
    () => getTennisLeagueOptionsForAdminTransfer(leagues),
    [leagues],
  );

  const [selectedLeagueId, setSelectedLeagueId] = useState<string>('');
  const [selectedSquadId, setSelectedSquadId] = useState<string>('');
  const [squads, setSquads] = useState<LeagueSquad[]>([]);
  const [usersByUid, setUsersByUid] = useState<Record<string, User>>({});
  const [tennisPool, setTennisPool] = useState<PlayerPoolEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [modalOpen, setModalOpen] = useState(false);

  const selectedLeague = useMemo(
    () => tennisLeagues.find((l) => l.id === selectedLeagueId) ?? null,
    [tennisLeagues, selectedLeagueId],
  );

  // Load squads + pool + user names whenever the selected league changes
  useEffect(() => {
    let cancelled = false;
    if (!selectedLeague) {
      setSquads([]);
      setUsersByUid({});
      setTennisPool([]);
      setSelectedSquadId('');
      return;
    }
    const run = async () => {
      setLoading(true);
      setError('');
      try {
        const [squadList, pool] = await Promise.all([
          tennisSquadService.getByLeague(selectedLeague.id),
          selectedLeague.playerPoolId
            ? tennisPlayerPoolService.getById(selectedLeague.playerPoolId)
            : Promise.resolve(null),
        ]);
        if (cancelled) return;
        setSquads(squadList);
        setTennisPool(pool?.players ?? []);
        const uniqueUids = Array.from(new Set(squadList.map((s) => s.userId)));
        const users = await userService.getByIds(uniqueUids);
        if (cancelled) return;
        const map: Record<string, User> = {};
        users.forEach((u) => { map[u.uid] = u; });
        setUsersByUid(map);
      } catch (e) {
        if (cancelled) return;
        const message = e instanceof Error ? e.message : 'Failed to load squads for league.';
        setError(message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [selectedLeague]);

  const squadOptions = useMemo(
    () => (selectedLeague ? getTennisSquadOptionsForAdminTransfer(squads, selectedLeague) : []),
    [squads, selectedLeague],
  );

  const selectedOption = squadOptions.find((o) => o.squad.id === selectedSquadId) ?? null;
  const selectedSquad = selectedOption?.squad ?? null;

  const targetUserName = useMemo(() => {
    if (!selectedSquad) return undefined;
    const u = usersByUid[selectedSquad.userId];
    return u?.displayName || u?.email || selectedSquad.squadName;
  }, [selectedSquad, usersByUid]);

  const canOpen = Boolean(
    user && selectedLeague && selectedSquad && selectedOption && !selectedOption.isDisabled,
  );

  const handleTransferComplete = (updated: LeagueSquad) => {
    setSquads((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
    setModalOpen(false);
  };

  return (
    <Paper sx={{ p: 2.5, mb: 3, borderRadius: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
        Admin Transfer — On Behalf
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Pick a tennis league and a squad, then perform a transfer through the
        same flow the player would use. The transfer is audit-stamped with your
        admin UID. Players with zero transfers remaining cannot be selected.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <FormControl fullWidth disabled={tennisLeagues.length === 0}>
          <InputLabel>Tennis League</InputLabel>
          <Select
            label="Tennis League"
            value={selectedLeagueId}
            onChange={(e) => {
              setSelectedLeagueId(e.target.value);
              setSelectedSquadId('');
            }}
          >
            <MenuItem value="">— Select league —</MenuItem>
            {tennisLeagues.map((l) => (
              <MenuItem key={l.id} value={l.id}>
                {l.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth disabled={!selectedLeague || loading}>
          <InputLabel>Squad</InputLabel>
          <Select
            label="Squad"
            value={selectedSquadId}
            onChange={(e) => setSelectedSquadId(e.target.value)}
          >
            <MenuItem value="">— Select squad —</MenuItem>
            {squadOptions.map((opt) => {
              const owner = usersByUid[opt.squad.userId];
              const ownerLabel = owner?.displayName || owner?.email || 'Unknown user';
              return (
                <MenuItem
                  key={opt.squad.id}
                  value={opt.squad.id}
                  disabled={opt.isDisabled}
                >
                  {opt.squad.squadName} · {ownerLabel} — {opt.transfersLeft === Infinity ? '∞' : Math.max(0, opt.transfersLeft)} left
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </Stack>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          variant="contained"
          disabled={!canOpen}
          onClick={() => setModalOpen(true)}
        >
          Open Transfer Flow
        </Button>
        {loading && <CircularProgress size={20} />}
        {selectedOption?.isDisabled && (
          <Typography variant="caption" color="warning.main">
            This squad has no transfers remaining.
          </Typography>
        )}
      </Box>

      {user && selectedLeague && selectedSquad && (
        <TennisTransferModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          league={selectedLeague}
          existingSquad={selectedSquad}
          tennisPool={tennisPool}
          onTransferComplete={handleTransferComplete}
          adminContext={{ actingUid: user.uid, targetUserName }}
        />
      )}
    </Paper>
  );
};

export default AdminTennisTransferPanel;
